import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import CreditScoreModal from "../Modals/CreditScoreModal";
import CreditScoreModal2 from "../Modals/CreditScoreModal2";

const CustomSpeedometer = ({ score, maxScore }) => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  const radius = 120;
  const stroke = 30;
  const centerX = 150;
  const centerY = 150; // semi-circle, so bottom is center
  const percent = Math.min(Math.max(score / maxScore, 0), 1);

  // 4 Segments: Red, Orange, Green, Teal
  const segmentColors = ['#F56565', '#ED8936', '#48BB78', '#38B2AC']; // Tailwind-ish colors
  const labels = [
    isTamil ? t('eligibilityScorecard.poor') : "POOR",
    isTamil ? t('eligibilityScorecard.average') : "AVERAGE",
    isTamil ? t('eligibilityScorecard.good') : "GOOD",
    isTamil ? t('eligibilityScorecard.excellent') : "EXCELLENT"
  ];

  // Helper to convert polar to cartesian
  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 180) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  const describeArc = (x, y, radius, startAngle, endAngle) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
      "M", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
    ].join(" ");
  };

  // Draw Segments
  const segments = segmentColors.map((color, i) => {
    const startAngle = i * 45; // 180 / 4
    const endAngle = (i + 1) * 45;
    const path = describeArc(centerX, centerY, radius, startAngle, endAngle);

    return (
      <path
        key={i}
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
      />
    );
  });

  // Segment Labels (POOR, AVERAGE...)
  const segmentLabels = labels.map((label, i) => {
    const angle = (i * 45) + 22.5; // Center of segment
    const pos = polarToCartesian(centerX, centerY, radius - 35, angle); // Moved inside
    return (
      <text
        key={`seg-label-${i}`}
        x={pos.x}
        y={pos.y}
        fill="white"
        fontSize="10"
        fontWeight="bold"
        textAnchor="middle"
        transform={`rotate(${angle - 90}, ${pos.x}, ${pos.y})`}
      >
        {label}
      </text>
    )
  });


  // Draw Ticks 0-100
  const ticks = [];
  for (let i = 0; i <= 100; i += 10) {
    const angle = (i / 100) * 180;
    const tickStart = polarToCartesian(centerX, centerY, radius - stroke / 2 - 5, angle);
    const tickEnd = polarToCartesian(centerX, centerY, radius - stroke / 2 + 5, angle);
    const textPos = polarToCartesian(centerX, centerY, radius - stroke - 15, angle);

    ticks.push(
      <g key={i}>
        <line
          x1={tickStart.x}
          y1={tickStart.y}
          x2={tickEnd.x}
          y2={tickEnd.y}
          fill="#ffffffff"
          strokeWidth="1"
        />
        <text
          x={textPos.x}
          y={textPos.y}
          fill="#ffffffff"
          fontSize="10"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {i}
        </text>
      </g>
    );
  }

  // Needle
  const needleAngle = percent * 180;

  return (
    <div style={{ position: 'relative', width: '100%', height: 'auto', maxWidth: '400px', margin: '0 auto' }}>
      <svg viewBox={`0 0 ${centerX * 2} ${centerY + 20}`} style={{ width: '100%', height: 'auto' }}>
        {segments}
        {segmentLabels}
        {ticks}
        <g style={{ transition: 'transform 1s ease-out', transformOrigin: `${centerX}px ${centerY}px`, transform: `rotate(${needleAngle}deg)` }}>
          <circle cx={centerX} cy={centerY} r="8" fill="#333" />
          <path d={`M ${centerX} ${centerY - 5} L ${centerX - (radius - 10)} ${centerY} L ${centerX} ${centerY + 5} Z`} fill="#333" />
        </g>
      </svg>
    </div>
  );
};


const EligibilityScorecardSection = ({ pageType = 'home_page' }) => {
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [score, setScore] = useState(0);
  const [result, setResult] = useState(null);
  const [showCreditScoreModal, setShowCreditScoreModal] = useState(false);
  const [showCreditScoreModal2, setShowCreditScoreModal2] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine if Tamil is selected
  const isTamil = i18n.language === 'ta';

  // Fetch dynamic questions and settings from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [questionsRes, settingsRes] = await Promise.all([
            axios.get(`/api/admin/eligibility-scorecard/questions/active?page_type=${pageType}`),
          axios.get(`/api/admin/eligibility-scorecard/settings?page_type=${pageType}`)
        ]);

        if (questionsRes.data.success) {
          const transformedQuestions = questionsRes.data.data.map((q, index) => ({
            id: `question_${q.id}`,
            question: q.question_text,
            type: q.question_type === 'text_input' ? 'text' : undefined,
            options: q.options.map(opt => ({
              label: opt.option_label,
              value: opt.option_value,
              points: parseFloat(opt.points)
            })),
            pointsLogic: q.question_type === 'text_input' ? (value) => {
              const num = parseFloat(value);
              if (isNaN(num)) return 0;
              return 0;
            } : undefined
          }));
          setQuestions(transformedQuestions);
        }

        if (settingsRes.data.success) {
          setSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching eligibility scorecard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
}, [pageType]);

  const sectionTitles = React.useMemo(() => {
    if (questions.length === 0) return [];

    const sections = [];
    let currentCategory = null;
    let startIndex = 0;

    questions.forEach((q, index) => {
      let category;
      if (index <= 7) category = isTamil ? "சொத்து மதிப்பீடு" : "Property Assessment";
      else if (index <= 10) category = isTamil ? "வருமான மதிப்பீடு" : "Income Assessment";
      else category = isTamil ? "மக்கள்தொகை மதிப்பீடு" : "Demographic Assessment";

      if (currentCategory !== category) {
        if (currentCategory !== null) {
          sections.push({ range: [startIndex, index - 1], title: currentCategory });
        }
        currentCategory = category;
        startIndex = index;
      }
    });

    if (currentCategory !== null) {
      sections.push({ range: [startIndex, questions.length - 1], title: currentCategory });
    }

    return sections;
  }, [questions, isTamil]);


  const currentSection =
    sectionTitles.find((s) => currentStep >= s.range[0] && currentStep <= s.range[1])?.title || "";


  const maxScore = questions.reduce((sum, q) => {
    if (q.options && q.options.length > 0) {
      return sum + Math.max(...q.options.map((o) => o.points));
    }
    return sum;
  }, 0);

  const handleSelect = (id, option) => {
    setResponses((prev) => ({ ...prev, [id]: option }));
    const otherScores = Object.entries(responses)
      .filter(([key]) => key !== id)
      .reduce((sum, [, opt]) => sum + (opt?.points || 0), 0);
    setScore(otherScores + option.points);
  };

  const handleManualInput = (id, value, logicFn) => {
    const points = logicFn(value);
    const updated = { value, points };
    setResponses((prev) => ({ ...prev, [id]: updated }));
    const total = Object.entries({ ...responses, [id]: updated }).reduce(
      (sum, [, opt]) => sum + (opt?.points || 0),
      0
    );
    setScore(total);
  };

  const nextStep = () => {
    if (currentStep < questions.length - 1) setCurrentStep(currentStep + 1);
    else setResult(true);
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  if (loading) {
    return (
      <section id="scorecard" className="py-20 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900">
        <div className="container mx-auto px-4">
          <div className="text-center text-white">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
            <p className="text-lg">{isTamil ? 'ஏற்றுகிறது...' : 'Loading eligibility scorecard...'}</p>
          </div>
        </div>
      </section>
    );
  }

  if (questions.length === 0) {
    return (
      <section id="scorecard" className="py-20 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900">
        <div className="container mx-auto px-4">
          <div className="text-center text-white">
            <p className="text-lg">
              {isTamil ? 'தகுதி கேள்விகளை ஏற்ற முடியவில்லை. பிறகு மீண்டும் முயற்சிக்கவும்.' : 'Unable to load eligibility questions. Please try again later.'}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="scorecard" className="py-20 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900">
      <div className="container mx-auto px-4">
        <motion.div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-white mb-4">
            {isTamil ? t('eligibilityScorecard.title') : (settings?.section_title || 'Your Journey to Home Ownership Starts Here')}
          </h2>
          <p className="text-blue-100 text-lg">
            {isTamil ? t('eligibilityScorecard.subtitle') : (settings?.section_subtitle || 'Take our quick eligibility test and see how close you are to your dream home.')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
          <div className="bg-white shadow-lg rounded-3xl p-8 h-full flex flex-col justify-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              {isTamil ? t('eligibilityScorecard.scorecard') : 'Scorecard'}
            </h3>

            <div className="flex flex-col items-center mb-10 w-full">
              <CustomSpeedometer score={score} maxScore={maxScore} />

              <div style={{ marginTop: 12, width: '100%', maxWidth: '400px' }}>
                <div className="flex items-center justify-between w-full text-[10px] text-gray-700 font-bold px-2 uppercase tracking-tighter">
                  <div className="flex-1 flex flex-col items-center">
                    <div className="w-full h-3 rounded-t-md" style={{ background: '#FF4E42', opacity: 0.9 }} />
                    <div className="mt-2">{isTamil ? t('eligibilityScorecard.poor') : 'BELOW'}</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center px-2">
                    <div className="w-full h-3 rounded-t-md" style={{ background: '#FFA500', opacity: 0.9 }} />
                    <div className="mt-2">{isTamil ? t('eligibilityScorecard.average') : 'AVERAGE'}</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center px-2">
                    <div className="w-full h-3 rounded-t-md" style={{ background: '#48BB78', opacity: 0.9 }} />
                    <div className="mt-2">{isTamil ? t('eligibilityScorecard.good') : 'GOOD'}</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center pl-2">
                    <div className="w-full h-3 rounded-t-md" style={{ background: '#38B2AC', opacity: 0.9 }} />
                    <div className="mt-2">{isTamil ? t('eligibilityScorecard.excellent') : 'EXCELLENT'}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                <span>
                  {isTamil ? t('eligibilityScorecard.question') : 'Question'} {currentStep + 1} {isTamil ? t('eligibilityScorecard.of') : 'of'} {questions.length}
                </span>
                <span className="text-indigo-700 font-bold">
                  {Math.round(((currentStep + 1) / questions.length) * 100)}% {isTamil ? t('eligibilityScorecard.complete') : 'Complete'}
                </span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2.5 shadow-inner">
                <div
                  className="h-2.5 rounded-full bg-indigo-700 transition-all duration-500 ease-out"
                  style={{
                    width: `${((currentStep + 1) / questions.length) * 100}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="h-full flex flex-col">
            {!result ? (
              <div className="bg-white shadow-lg rounded-3xl p-8 h-full flex flex-col justify-center relative min-h-[400px]">
                <div className="absolute top-6 left-6">
                  <span className="text-gray-400 font-bold text-xs uppercase tracking-widest">
                    {currentStep + 1} / {questions.length}
                  </span>
                </div>

                {currentSection && (
                  <div className="absolute top-6 right-6">
                    <span className="inline-block bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full text-[10px] font-black uppercase shadow-sm tracking-widest">
                      {currentSection}
                    </span>
                  </div>
                )}

                <h3 className="text-2xl font-bold text-gray-900 mb-10 text-center mt-8 leading-tight">
                  {questions[currentStep].question}
                </h3>

                {questions[currentStep].type === 'text' || !questions[currentStep].options || questions[currentStep].options.length === 0 ? (
                  <div className="mt-6" key={questions[currentStep].id}>
                    <input
                      type="text"
                      placeholder={isTamil ? "உங்கள் பதிலை இங்கே தட்டச்சு செய்யவும்..." : "Type your answer here..."}
                      className="w-full border-2 border-gray-100 rounded-2xl p-4 focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all text-lg font-medium"
                      value={responses[questions[currentStep].id]?.value || ''}
                      onChange={(e) =>
                        handleManualInput(
                          questions[currentStep].id,
                          e.target.value,
                          questions[currentStep].pointsLogic
                        )
                      }
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {questions[currentStep].options.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSelect(questions[currentStep].id, option)}
                        className={`p-5 border-2 rounded-2xl text-left transition-all duration-300 font-bold text-sm leading-snug shadow-sm ${responses[questions[currentStep].id]?.value === option.value
                          ? 'border-primary-600 bg-primary-50 text-primary-700 transform scale-[1.02] shadow-md'
                          : 'border-gray-50 hover:border-primary-200 hover:bg-gray-50'
                          }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex justify-between items-center mt-12">
                  <button
                    onClick={prevStep}
                    disabled={currentStep === 0}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest transition-all ${currentStep === 0
                      ? 'text-gray-200 cursor-not-allowed'
                      : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'
                      }`}
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    <span>{isTamil ? t('eligibilityScorecard.previous') : 'Previous'}</span>
                  </button>

                  <button
                    onClick={nextStep}
                    disabled={!responses[questions[currentStep].id]}
                    className={`flex items-center space-x-3 px-10 py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all shadow-lg ${!responses[questions[currentStep].id]
                      ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                      : 'bg-[#ed2636] hover:bg-primary-700 text-white transform hover:scale-105 active:scale-95'
                      }`}
                  >
                    <span>{currentStep === questions.length - 1 ? (isTamil ? t('eligibilityScorecard.getResult') : 'Get Result') : (isTamil ? t('eligibilityScorecard.next') : 'Next')}</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-3xl p-10 text-center shadow-2xl border-4 border-primary-50 h-full flex flex-col justify-center">
               {/* <div className="inline-flex items-center justify-center w-24 h-24 bg-primary-600 rounded-full mb-8 shadow-large animate-bounce">
                  <CheckCircleIcon className="h-12 w-12 text-white" />
                </div> */}
                 <h2 className="text-2xl font-black text-gray-900 mb-6 tracking-tight">
                  {isTamil ? t('eligibilityScorecard.assessmentComplete') : (settings?.result_title || 'Assessment Complete!')}
                </h2>

                <div className="flex justify-center mb-10">
                  <button
                    onClick={() => setShowCreditScoreModal2(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-xs tracking-widest px-8 py-5 rounded-2xl shadow-xl transition-all hover:scale-105 active:scale-95"
                  >
                    {isTamil ? t('eligibilityScorecard.checkCreditScore') : 'Check Credit Score'}
                  </button>
                </div>

                <p className="text-xl text-gray-600 font-medium leading-relaxed">
                  {isTamil ? 'உங்கள் இடுபொருள்களின் அடிப்படையில் உங்கள் தகுதி மதிப்பெண் கணக்கிடப்படுகிறது.' : (settings?.result_description || 'Your eligibility score is based on your inputs.')}
                </p>
              </div>
            )}
          </div>
        </div>

        <CreditScoreModal
          isOpen={showCreditScoreModal}
          onClose={() => setShowCreditScoreModal(false)}
        />
        <CreditScoreModal2
          isOpen={showCreditScoreModal2}
          onClose={() => setShowCreditScoreModal2(false)}
           answers={responses}
          score={score}
        />
      </div>
    </section>
  );
};

export default EligibilityScorecardSection;
